import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import api from "@/common/utils/api";
import { featureQueryKeys } from "@/feature/data/featureQueryKeys";
import {
  AttributeField,
  userSystemAttributeFields,
} from "@reflag/shared/attributeFilter";

const cacheTime = 30 * 1000; // 30 seconds

type PrefixType = "company" | "user" | "other";

export function useFeatureTargetingContextAttributeKeys(
  prefix?: PrefixType,
  filter?: string,
  environmentId?: string,
) {
  const { appId, envId: activeEnvId } = useEnvironment();
  const envId = environmentId ?? activeEnvId;

  return useQuery({
    queryKey: featureQueryKeys.targetingContextAttributeKeys(
      appId,
      envId,
      prefix,
      filter,
    ),

    queryFn: () =>
      api
        .get<"/apps/:appId/features/targeting/context-keys">(
          `/apps/${appId}/features/targeting/context-keys`,
          {
            params: {
              envId: envId!,
              prefix: prefix!,
              filter,
              includeTypes: true,
            },
          },
        )
        .then((res) => {
          const userSystemAttributesByKey = new Map<string, AttributeField>(
            userSystemAttributeFields.map((attr) => [attr.key, attr]),
          );
          const attributes = res.data.flatMap((attr) => {
            // The API returns unprefixed keys. Preserve dots inside attribute names.
            const key = typeof attr === "string" ? attr : attr.key;
            const systemAttribute =
              prefix === "user"
                ? userSystemAttributesByKey.get(key)
                : undefined;
            if (prefix === "user" && key === "id") return [];

            return [
              systemAttribute ??
                ({
                  key,
                  label: key,
                  type: undefined,
                  observedTypes:
                    typeof attr === "string" ? [] : attr.observedTypes,
                  icon: undefined,
                  system: false,
                } satisfies AttributeField),
            ];
          });

          return [
            ...(prefix === "user" ? userSystemAttributeFields : []),
            ...attributes,
          ].filter(
            (attr, index, allAttributes) =>
              allAttributes.findIndex(({ key }) => key === attr.key) === index,
          );
        }),

    gcTime: cacheTime,
    staleTime: cacheTime,
    placeholderData: keepPreviousData,
    enabled: !!appId && !!envId && !!prefix,
  });
}

export function useFeatureTargetingContextAttributeValues(
  prefix?: PrefixType,
  key?: string,
  filter?: string,
) {
  const { appId, envId } = useEnvironment();

  return useQuery({
    queryKey: featureQueryKeys.targetingContextAttributeValues(
      appId,
      envId,
      prefix,
      key,
      filter,
    ),

    queryFn: () =>
      api
        .get<"/apps/:appId/features/targeting/context-values">(
          `/apps/${appId}/features/targeting/context-values`,
          {
            params: {
              envId: envId!,
              key: `${prefix}.${key}`,
              filter,
            },
          },
        )
        .then((res) =>
          res.data.map((value: string) => ({ label: value, value })),
        ),

    gcTime: cacheTime,
    staleTime: cacheTime,
    placeholderData: keepPreviousData,
    enabled: !!appId && !!envId && !!prefix && !!key,
  });
}
